/**
 * 홈 화면 하단의 배너 캐러셀.
 *
 * StudentHome 이 useGetBannerMain 으로 받아온 bannerList 를 그대로 넘겨 렌더한다.
 * bannerList 가 비어 있을 때를 대비해 StudentHome 쪽에서 testBanner 1장짜리
 * 기본값을 미리 넣어 두므로, 이 컴포넌트 자체는 빈 배열 처리를 하지 않는다.
 */
import Image from "next/image";
import React, { useState, useEffect } from "react";
import LeftChevron from "../icons/home/LeftChevron";
import RightChevron from "../icons/home/RightChevron";
import { BannerItem } from "@/fetcher/home/useGetBannerMain";

interface CarouselProps {
  bannerList: BannerItem[];
}

/** 배너 이미지를 4초마다 자동 전환하는 캐러셀. 배너가 2개 이상일 때만 좌우 화살표를 보여준다. */
const Carousel: React.FC<CarouselProps> = ({ bannerList }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // 4초마다 자동으로 배너를 전환하는 useEffect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === bannerList.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000); // 4000ms = 4초

    return () => {
      clearInterval(interval); // 컴포넌트가 언마운트될 때 인터벌을 정리
    };
  }, [bannerList.length]);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === bannerList.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? bannerList.length - 1 : prevIndex - 1
    );
  };

  const openInNewTab = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const imageUrl = bannerList[currentIndex]?.imageUrl;
  const imageSrc =
    imageUrl && imageUrl.trim() !== "" ? imageUrl : "/images/testBanner.avif";

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => openInNewTab(bannerList[currentIndex]?.url ?? "")}
      className="relative flex justify-center items-center w-full bg-primary-muted rounded-[20px] overflow-hidden"
    >
      {/* 이미지 슬라이드 */}
      <div className="relative flex items-center justify-center w-full h-full cursor-pointer">
        <Image
          src={imageSrc}
          alt={`Slide ${currentIndex + 1}`}
          width={1200}
          height={464}
          sizes="(max-width: 768px) 100vw, (max-width: 1280px) 90vw, 1200px"
          className="rounded-[20px]"
          priority // LCP 이미지는 우선적으로 로드되도록 priority 설정
          loading="eager" // LCP 이미지는 즉시 로드되도록 eager 설정
        />
      </div>

      {/* 배너 이미지가 2개 이상인 경우만, 해당 버튼이 노출됨 */}
      {bannerList.length >= 2 && isHovered && (
        <div>
          {/* 왼쪽 화살표 버튼 */}
          <button
            onClick={(e) => {
              e.stopPropagation(); // 이벤트 버블링 방지
              prevSlide();
            }}
            className="absolute left-[40px] top-1/2 transform -translate-y-1/2"
          >
            <LeftChevron />
          </button>
          {/* 오른쪽 화살표 버튼 */}
          <button
            onClick={(e) => {
              e.stopPropagation(); // 이벤트 버블링 방지
              nextSlide();
            }}
            className="absolute right-[40px] top-1/2 transform -translate-y-1/2"
          >
            <RightChevron />
          </button>
        </div>
      )}

      {/* 페이지 번호 표시 */}
      <div className="absolute bottom-[32px] right-[28px] bg-disabled text-white text-14 font-bold px-[16px] py-[4px] rounded-full">
        {currentIndex + 1}/{bannerList.length}
      </div>
    </div>
  );
};

export default Carousel;
